import { LauncherMessages } from "@languages/interfaces";
import { Module } from "./types";

/**
 * Visual + copy metadata for a launcher product. The backend owns which
 * products exist and their contracted status (`tenantProducts` query); the
 * frontend owns the presentation (accent color and i18n copy).
 */
export type ModulePresentation = Omit<Module, "state" | "url" | "moduleId">;

/**
 * Presentation metadata keyed by the backend module alias. The key order also
 * defines the order the cards are rendered in the launcher grid (within each
 * state group — see the stable sort in index.tsx). Intended product order:
 * TECH, CRM, DISPATCH, BATCH, FLEET, REPORTS, then the remaining products.
 */
export const MODULE_PRESENTATION: Record<string, ModulePresentation> = {
  TOPCON_TECH: {
    id: "tech",
    label: "TECH",
    description: LauncherMessages.launcherModuleDescTech,
    accent: "#8f8f8f"
  },
  TOPCON_CRM: {
    id: "crm",
    label: "CRM",
    description: LauncherMessages.launcherModuleDescCrm,
    accent: "#CC0099"
  },
  TOPCON_DISPATCH: {
    id: "dispatch",
    label: "DISPATCH",
    description: LauncherMessages.launcherModuleDescDispatch,
    accent: "#D52924"
  },
  TOPCON_BATCH: {
    id: "batch",
    label: "BATCH",
    description: LauncherMessages.launcherModuleDescBatch,
    accent: "#CC6600"
  },
  TOPCON_FLEET: {
    id: "fleet",
    label: "FLEET",
    description: LauncherMessages.launcherModuleDescFleet,
    accent: "#D52924"
  },
  TOPCON_REPORTS: {
    id: "reports",
    label: "REPORTS",
    description: LauncherMessages.launcherModuleDescReports,
    accent: "#FF6B6B"
  },
  // FMX + DRIVER are always sold together, so the Launcher collapses them into a
  // single grouped card (see the grouping step in index.tsx). They are declared
  // as two separate presentation entries here (one per backend alias); the grid
  // merges them at render time.
  TOPCON_FMX: {
    id: "fmx",
    label: "FMX",
    description: LauncherMessages.launcherModuleDescFmx,
    accent: "#D52924"
  },
  TOPCON_DRIVER: {
    id: "driver",
    label: "DRIVER",
    description: LauncherMessages.launcherModuleDescDriver,
    accent: "#FF9900"
  },
  TOPCON_CUSTOMER: {
    id: "customer",
    label: "CUSTOMER",
    description: LauncherMessages.launcherModuleDescCustomer,
    accent: "#E0A100"
  },
  TOPCON_BI: {
    id: "bi",
    label: "BI",
    description: LauncherMessages.launcherModuleDescBi,
    accent: "#8a8a2a"
  },
  TOPCON_BILLING: {
    id: "billing",
    label: "BILLING",
    description: LauncherMessages.launcherModuleDescBilling,
    accent: "#6FA61C"
  },
  TOPCON_FINANCES: {
    id: "finances",
    label: "FINANCES",
    description: LauncherMessages.launcherModuleDescFinances,
    accent: "#6FA61C"
  },
  TOPCON_PURCHASE: {
    id: "purchase",
    label: "PURCHASE",
    description: LauncherMessages.launcherModuleDescPurchase,
    accent: "#6FA61C"
  },
  TOPCON_BACKOFFICE: {
    id: "backoffice",
    label: "BACKOFFICE",
    description: LauncherMessages.launcherModuleDescBackoffice,
    accent: "#6FA61C"
  },
  // Backend filters TOPCON_IDENTITY out of the Launcher product list, so this
  // entry never renders on the Launcher grid — it exists so admin-side
  // surfaces (e.g. ModuleAccessSection) get a proper label + accent + icon.
  TOPCON_IDENTITY: {
    id: "identity",
    label: "IDENTITY",
    description: LauncherMessages.launcherModuleDescIdentity,
    // The navbar Identity icon uses a warm gradient (orange → yellow → lime → yellow → orange);
    // a solid amber/gold is the closest single-color approximation of the perceived hue.
    accent: "#CC9933"
  }
};
